import { get } from 'lodash';
import React from 'react';

import SystemMessage from './SystemMessage';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import { Link } from 'libs/router';

import {
  SUBCAMPAIGN_OFFER_TYPE_OFFER,
  getSubcampaignOfferDisplayBudget
} from 'utils/subcampaign';
import { isOfferUpdateSystemMessage } from 'utils/messages';
import { __t, applyTranslations } from 'utils/language';

import type { Campaign, MessengerMessage } from 'types';

applyTranslations(require('./system-message.i18n.json'));

const getOfferSuffixType = (offer) => (offer.type === SUBCAMPAIGN_OFFER_TYPE_OFFER ? 'budget' : 'other');

const getOfferSuffix = (updatedOffer, offer) => {
  const updatedOfferType = getOfferSuffixType(updatedOffer);
  const offerType = getOfferSuffixType(offer);

  return `${offerType}_${updatedOfferType}`;
};

type Props = {
  campaign: Campaign,
  text: string,
  message: MessengerMessage,
  influencerActions: React.ReactNode,
  isBrand: boolean
};

class SystemChatMessageNegotiationsReopened extends SystemMessage<Props> {
  renderDescription(offer, isUpdatedOffer = false) {
    const title = isUpdatedOffer
      ? __t('system_message.description_title')
      : __t('system_message.description_old_title');

    if (offer.type === SUBCAMPAIGN_OFFER_TYPE_OFFER) {
      return (
        <div className="messages-system-message__offer-title">
          {`${title}: ${getSubcampaignOfferDisplayBudget(offer)}`}
        </div>
      );
    }

    return (
      <div>
        <div className="messages-system-message__other-title">
          {title}:
        </div>
        <RichTextEditor
          key={JSON.stringify(offer.perks)}
          value={offer.perks}
          canEdit={false}
        />
      </div>
    );
  }

  // only after the offer was updated after it was accepted by the influencer
  renderRenegotiationOffer(updatedOffer, offer) {
    const subtitle = __t(`system_message.subtitle_${getOfferSuffix(updatedOffer, offer)}`);

    return (
      <React.Fragment>
        <div className="messages-system-message__paragraph">
          {subtitle}
        </div>
        <div className="messages-system-message__offer messages-system-message__offer--new">
          {this.renderDescription(updatedOffer, true)}
        </div>
        <div className="messages-system-message__offer messages-system-message__offer--old">
          {this.renderDescription(offer)}
        </div>
      </React.Fragment>
    );
  }

  // if brand updates the offer while it's still pending
  renderUpdatedOffer(offer) {
    const offerType = getOfferSuffixType(offer);
    const title = __t(`system_message.pending_${offerType}_title`);

    const description = offer.type === SUBCAMPAIGN_OFFER_TYPE_OFFER
      ? (
        <div className="messages-system-message__offer-title">
          {`${title}: ${getSubcampaignOfferDisplayBudget(offer)}`}
        </div>
      )
      : (
        <div>
          <div className="messages-system-message__offer-title">
            {title}:
          </div>
          <RichTextEditor
            key={JSON.stringify(offer.perks)}
            value={offer.perks}
            canEdit={false}
          />
        </div>
      );

    return (
      <div className="messages-system-message__offer messages-system-message__offer--updated">
        {description}
      </div>
    );
  }

  render() {
    const {
      campaign,
      influencerActions,
      text,
      isBrand,
      message
    } = this.props;

    const isOfferUpdated = isOfferUpdateSystemMessage(message);
    const subcampaign = get(message, 'metadata.subcampaign') || {};
    const { offer, updatedOffer } = subcampaign;

    const hasNewOffer = updatedOffer && offer;

    const disclaimerType = isBrand ? 'brand' : 'creator';

    const disclaimer = hasNewOffer
      ? __t(`system_message.renegotiation_${disclaimerType}_disclaimer`)
      : __t(`system_message.expired_${disclaimerType}_disclaimer`);
    const disclaimerTooltip = hasNewOffer
      ? __t(`system_message.renegotiation_disclaimer_${disclaimerType}_tooltip`)
      : __t(`system_message.expiration_disclaimer_${disclaimerType}_tooltip`);

    return (
      <React.Fragment>
        {this.renderIcon(isOfferUpdated ? 'report-inside-circle-custom-fg' : 'letter-inside-circle-custom-fg')}
        <div className="messages-system-message__container">
          <div className="messages-system-message__bubble">
            <div className="messages-system-message__heading">
              {text}
            </div>
            {
              isOfferUpdated && (
                <React.Fragment>
                  {hasNewOffer && this.renderRenegotiationOffer(updatedOffer, offer)}
                  {!updatedOffer && offer && this.renderUpdatedOffer(offer)}
                </React.Fragment>
              )
            }
            <div className="messages-system-message__paragraph">
              <Link
                to={`/campaigns/${campaign.id}/${isBrand ? 'subcampaigns' : 'details'}/${subcampaign.id}`}
                target="_blank"
                className="messages-system-message__link"
              >
                {__t('system_message.view_the_brief')}
              </Link>
            </div>
            {!isBrand && influencerActions && this.renderActions(influencerActions)}
            {this.renderDisclaimer(disclaimer, disclaimerTooltip)}
          </div>
        </div>
        {this.renderFooter()}
      </React.Fragment>
    );
  }
}

export default SystemChatMessageNegotiationsReopened;
